<script>
  import { BaseEdge, EdgeLabel } from "@xyflow/svelte";
  import * as d3 from "d3";

  export let data;
  export let markerEnd;
  export let sourceY;
  export let targetY;
  let path;
  let labelPosition;
  let labelLines = [];

  $$restProps;

  $: points = alignToNodeBounds(data.points, sourceY, targetY);
  $: labelPosition = calculateLabelPosition(points);
  $: path = createEdgePath(trimEdgePoints(points));
  $: labelLines = String(data.label || "")
    .split("\n")
    .filter((line) => line && line !== "undefined");

  function alignToNodeBounds(points, sourceY, targetY) {
    if (!data.alignToNodeBounds || points?.length < 2 ||
        !Number.isFinite(sourceY) || !Number.isFinite(targetY) ||
        points[0].y >= points[points.length - 1].y) return points;
    // The rendered node can be shorter than its estimated Dagre layout height.
    return points.map((point, index) =>
      index === 0 ? { ...point, y: sourceY } :
      index === points.length - 1 ? { ...point, y: targetY } : point,
    );
  }

  function createEdgePath(points) {
    const line = d3
      .line()
      .curve(d3.curveMonotoneY)
      .x((d) => d.x)
      .y((d) => d.y);

    return line(points);
  }

  function trimEdgePoints(points) {
    if (!points || points.length < 2) return points;
    const trimmed = points.map((point) => ({ ...point }));
    trimPoint(trimmed[0], trimmed[1]);
    trimPoint(trimmed[trimmed.length - 1], trimmed[trimmed.length - 2]);
    return trimmed;
  }

  function trimPoint(point, neighbor) {
    const dx = neighbor.x - point.x;
    const dy = neighbor.y - point.y;
    const length = Math.hypot(dx, dy);
    if (!length) return;
    const inset = 16;
    point.x += (dx / length) * inset;
    point.y += (dy / length) * inset;
  }

  function calculateLabelPosition(points) {
    if (!points?.length) return { x: 0, y: 0 };
    if (points.length === 1) return points[0];

    const segments = points.slice(1).map((point, index) => {
      const start = points[index];
      return {
        start,
        end: point,
        length: Math.hypot(point.x - start.x, point.y - start.y),
      };
    });
    const halfway =
      segments.reduce((total, segment) => total + segment.length, 0) / 2;
    let traversed = 0;

    for (const segment of segments) {
      if (traversed + segment.length >= halfway) {
        const ratio = segment.length
          ? (halfway - traversed) / segment.length
          : 0;
        return {
          x: segment.start.x + (segment.end.x - segment.start.x) * ratio,
          y: segment.start.y + (segment.end.y - segment.start.y) * ratio,
        };
      }
      traversed += segment.length;
    }

    return points[points.length - 1];
  }

</script>

<BaseEdge
  {path}
  {markerEnd}
  style="stroke-width: 2; stroke:black; {data.dashed ? 'stroke-dasharray: 8 6;' : ''}"
/>
{#if labelLines.length}
  <EdgeLabel>
    <div
      style:transform="translate(-50%, -50%) translate({labelPosition.x}px,{labelPosition.y}px)"
      class="edge-label"
      data-edge-id={data.id}
    >
      <span class="edge-label-text">
        <span class="edge-label-main">{labelLines[0]}</span>
      </span>
    </div>
  </EdgeLabel>
{/if}

<style>
  .edge-label {
    position: absolute;
    display: flex;
    align-items: center;
    gap: 8px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 12px;
    background: white;
    color: #000;
    padding: 8px 12px;
    min-width: max-content;
  }

  .edge-label-text {
    display: flex;
    flex-direction: column;
    align-items: center;
    line-height: 1.05;
  }

  .edge-label-main {
    font-size: 14px;
    font-weight: 700;
  }

  :global(.svelte-flow__edges) {
    z-index: 50 !important;
  }

  :global(.svelte-flow__edgelabel-renderer) {
    z-index: 100 !important;
  }
</style>
